// Angular imports
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// 3rd party imports
import { DxValidationGroupComponent } from 'devextreme-angular';
import { forkJoin } from 'rxjs';

// CC-Common imports
import { Utils } from 'cc-common';

// Env Imports
import { environment } from '../../../environments/environment';

// Component imports
import { RecaptchaBaseComponent } from '../_base/recaptcha-base.component';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { CommunityService } from '../../_services/data-services/community.service';
import { QaUserService } from '../../_services/data-services/qa-user.service';
import { UserService } from '../../_services/data-services/user.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { UserBackgroundService } from '../../_services/shared-services/user-background.service';

// Model Imports
import { CommunitySelectorResultDto } from '../../_models/community-selector-result-dto';
import { LoginParameterDto } from '../../_models/login-parameter-dto';
import { UserInfoResultDto } from '../../_models/user-info-result-dto';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent extends RecaptchaBaseComponent implements OnInit {
    @ViewChild('loginValidationGroup', { static: false }) loginValidationGroup: DxValidationGroupComponent;

    loginAttempts = 0;
    noOfAllowedAttempts = 5;
    returnUrl: string;
    loadingVisible = false;
    isSmallScreen = false;
    btnLoginAndResetWidth = 130;
    passwordMode: string;
    passwordShowButton: object;
    passwordHideButton: object;
    loginParam: LoginParameterDto = new LoginParameterDto();
    communities: CommunitySelectorResultDto[];

    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly screenService: ScreenService,
        private readonly helperService: HelperService,
        private readonly qaUserService: QaUserService,
        protected readonly errorService: ServerErrorService,
        private readonly communityService: CommunityService,
        private readonly userBackgroundService: UserBackgroundService
    ) {
        super(errorService);
        this.passwordMode = 'password';
        this.passwordShowButton = {
            icon: 'fas fa-eye',
            type: 'default',
            stylingMode: 'text',
            hint: 'Show Password',
            onClick: () => {
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
            }
        };
        this.passwordHideButton = {
            icon: 'fas fa-eye-slash',
            type: 'default',
            stylingMode: 'text',
            hint: 'Hide Password',
            onClick: () => {
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
            }
        };
    }

    ngOnInit() {
        this.route.queryParams.subscribe((params) => {
            this.returnUrl = this.route.snapshot.queryParams['returnUrl'];
            if (this.authService.isLoggedIn) {
                if (this.returnUrl && this.returnUrl.indexOf('login') !== -1) {
                    this.returnUrl = null;
                }
                if (!this.userService.getCurrentUserId()) {
                    this.navigateAfterLoginSuccess();
                } else {
                    this.navigateToRoute();
                }
            }
        });

        this.updateScreenSize();
        this.screenService.changed.subscribe((next) => {
            this.updateScreenSize();
        });
    }

    btnLoginClick(event: any): void {
        const validator = this.loginValidationGroup.instance.validate();
        if (validator.isValid) {
            if (this.loginAttempts < this.noOfAllowedAttempts || this.isValidRecaptcha()) {
                this.loginParam.recaptchaToken = this.recaptchaToken;
                this.doLogin().then((success: boolean) => {
                    if (success) {
                        this.loginAttempts = 0;
                        this.userBackgroundService.logActivity('login', 'LOGIN', new Date(), 'success');
                    } else {
                        this.loginAttempts += 1;
                        this.resetRecaptcha();
                    }
                });
            }
        }
    }

    btnResetPasswordClick(event: Event): void {
        this.router.navigate(['user', 'forgotpassword']);
    }

    linkChooseDifferentPortalClick() {
        window.location.href = environment.portalSelectionUrl;
    }

    private doLogin(): Promise<boolean> {
        return new Promise((resolve) => {
            this.loadingVisible = true;
            this.authService.clearAuthLocalStorageData();
            this.authService.login(this.loginParam).subscribe(
                (next) => {
                    resolve(true);
                    this.loadingVisible = false;
                    this.navigateAfterLoginSuccess();
                },
                (error) => {
                    resolve(false);
                    this.loadingVisible = false;
                    if (error.status === 404) {
                        this.errorService.updateSubjectErrorMessage(
                            'Invalid login credentials. Please verify your user id and password!'
                        );
                    } else {
                        this.processApiError(error);
                    }
                }
            );
        });
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        if (this.isSmallScreen) {
            this.btnLoginAndResetWidth = 110;
        } else {
            this.btnLoginAndResetWidth = 130;
        }
    }

    private navigateAfterLoginSuccess(): void {
        this.userService.userPortalInfo().subscribe((info: UserInfoResultDto) => {
            if (!!info && !!info.displayName) {
                this.userService.setCurrentUserInfo(info);
                forkJoin([this.userService.loadUserSettings(), this.qaUserService.loadQaUsers()]).subscribe(() => {
                    this.authService.updateSubjectLoginSuccess(true);
                    // setTimeout(() => {
                    this.getOnlyOneCommunityAccess().then((success) => {
                        if (Utils.isTrue(success)) {
                            this.returnUrl = `community/${this.communities[0].communityId}/home`;
                        }
                        this.navigateToRoute();
                    });
                    // }, 1000);
                });
            } else {
                this.authService.logOut();
                this.errorService.updateSubjectErrorMessage(`You do not have access to management / board portal.`);
            }
        });
    }

    private navigateToRoute(): void {
        this.router.navigateByUrl(this.returnUrl || '/home').then((flag) => {
            if (flag) {
                setTimeout(
                    () =>
                        this.helperService.subjectAutoSelectHomeMenuItem.next(
                            (this.returnUrl || '/home').split('?')[0]
                        ),
                    250
                );
            }
        });
    }

    private getOnlyOneCommunityAccess(): Promise<boolean> {
        return new Promise((resolve) => {
            this.loadingVisible = true;
            this.communityService.getCommunities().subscribe(
                (communities: CommunitySelectorResultDto[]) => {
                    this.communities = communities;
                    if (Utils.notNullOrEmpty(this.communities) && Utils.equals(this.communities.length, 1)) {
                        resolve(true);
                    } else {
                        const activeCommunities = this.communities.filter((d) => Utils.isTrue(d.active));
                        if (Utils.notNullOrEmpty(activeCommunities) && Utils.equals(activeCommunities.length, 1)) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    }
                    this.loadingVisible = false;
                },
                (error) => {
                    resolve(false);
                    this.loadingVisible = false;
                    this.errorService.displayError(error);
                }
            );
        });
    }
}
